import React, { useState, useEffect } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  SearchIcon, 
  FilterIcon,
  EyeIcon,
  RefreshCw 
} from 'lucide-react';
import api from '../../axios'; // Axios instance
import PaymentConfirmationModal from './PaymentConfirmationModal'; // Adjust the import path as necessary
const ProjectDataTable = ({ user }) => {
  const [recentProjects, setRecentProjects] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [projectStats, setProjectStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'updated_at',
    direction: 'desc'
  });

  useEffect(() => {
    fetchProjects();
    fetchSystemSettings();
  }, [user]);

  const fetchProjects = () => {
    setLoading(true);
    // Fetch projects from API
    api.get(`/projects?user_id=${user.id}`)
      .then((response) => {
        const projects = response.data;
        setRecentProjects(projects);

        // Calculate project stats
        const stats = {
          total: projects.length,
          approved: projects.filter((p) => p.status === "approved").length,
          pending: projects.filter((p) => p.status === "pending").length,
          rejected: projects.filter((p) => p.status === "rejected").length,
        };

        setProjectStats(stats);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error.response?.data || error.message);
        setLoading(false);
      });
  };
  const fetchSystemSettings = async () => {
    try {
      const res = await api.get('/system-settings'); // Make sure this endpoint returns the settings
      setSystemSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch system settings', err);
    }
  };

  const openPaymentModal = (project, type) => {
    const fee = type === 'processing_fee'
      ? parseFloat(systemSettings?.processing_fee || 0)
      : parseFloat(systemSettings?.approval_fee || 0);
  
    const reference = `PRO-${project.id}-${type}-${Date.now().toString().slice(-4)}`;
  
    setPaymentData({
      projectName: project.name,
      projectId: project.id,
      feeType: type,
      amount: fee,
      reference,
    });
    setShowPaymentModal(true);
  };

  

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted projects
  const sortedProjects = [...recentProjects].sort((a, b) => {
    if (sortConfig.key === 'updated_at') {
      return sortConfig.direction === 'asc' 
        ? new Date(a.updated_at) - new Date(b.updated_at)
        : new Date(b.updated_at) - new Date(a.updated_at);
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filter projects based on search term
  const filteredProjects = sortedProjects.filter(project => 
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to determine status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Function to render sort indicator
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon size={16} className="inline ml-1" />
    ) : (
      <ChevronDownIcon size={16} className="inline ml-1" />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with stats */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <h2 className="text-lg font-medium text-gray-900 mb-3 sm:mb-0">Recent Projects</h2>
        <div className="flex space-x-4 items-center text-sm">
          <div className="px-3 py-1 rounded-full bg-blue-50">{projectStats.total} Total</div>
          <div className="px-3 py-1 rounded-full bg-green-50 text-green-700">{projectStats.approved} Approved</div>
          <div className="px-3 py-1 rounded-full bg-yellow-50 text-yellow-700">{projectStats.pending} Pending</div>
          <div className="px-3 py-1 rounded-full bg-red-50 text-red-700">{projectStats.rejected} Rejected</div>
        </div>
      </div>
      
      {/* Search and filters */}
      <div className="px-6 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 bg-gray-50">
        <div className="relative w-full sm:w-64 mb-3 sm:mb-0">
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute left-3 top-2.5 text-gray-400">
            <SearchIcon size={18} />
          </div>
        </div>
        <div className="flex items-center">
          <button 
            onClick={fetchProjects}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <RefreshCw  size={16} className="mr-1" /> Refresh
          </button>
          <button className="ml-2 flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300">
            <FilterIcon size={16} className="mr-1" /> Filter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('name')}
              >
                Project Name {getSortIcon('name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('updated_at')}
              >
                Last Updated {getSortIcon('updated_at')}
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Processing Fee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Approval Fee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                  <div className="mt-2">Loading projects...</div>
                </td>
              </tr>
            ) : filteredProjects.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                  No projects found matching your search
                </td>
              </tr>
            ) : (
              filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{project.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(project.updated_at).toLocaleString('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {project.processing_fee === 'paid' ? (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-700">
                        Paid
                      </span>
                    ) : (
                      <button
                        onClick={() => openPaymentModal(project, 'processing_fee')}
                        className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                      >
                        Pay Now
                      </button>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {project.approval_fee === 'paid' ? (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-700">
                        Paid
                      </span>
                    ) : (
                      <button
                        onClick={() => openPaymentModal(project, 'approval_fee')}
                        className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                      >
                        Pay Now
                      </button>
                    )}
                  </td>


                  <td className="px-6 py-4 whitespace-nowrap text-sm flex gap-2">
                    <a href={`/project-view/${project.id}`} className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <EyeIcon size={16} className="mr-1" /> View
                    </a>

                    {project.status === 'rejected' && (
                      <a href={`/projects/${project.id}/edit`} className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                        Edit
                      </a>
                    )}
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Footer with pagination placeholder */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center">
        <div>
          Showing {filteredProjects.length} of {recentProjects.length} projects
        </div>
        <div className="flex space-x-1">
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50">Previous</button>
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white text-blue-600 font-medium">1</button>
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50">Next</button>
        </div>
      </div>


      {showPaymentModal && (
        <PaymentConfirmationModal
          paymentDetails={paymentData}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
    </div>


  );
};

export default ProjectDataTable;